import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import FeedbackModal from './components/FeedbackModal';
import InterviewModal from './components/InterviewModal';
import AdvancedAnalysis from './components/AdvancedAnalysis';
import ScoreAdjustment from './components/ScoreAdjustment';
import EnhancedScoringDisplay from './components/EnhancedScoringDisplay';
import { apiFetch, getBackendUrl, getUserFacingErrorMessage, throwIfNotOk } from './services/apiClient';
import { resolveCandidateContact } from './utils/candidateAnalysisUtils';
import './JobsPage.css';
import './components/interview-evaluation.css';
import './components/ScreeningAutomationPanel.css';
import InterviewEvaluationModal from './components/InterviewEvaluationModal';
import QuestionnaireModal from './components/QuestionnaireModal';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';
import FileViewerModal from './components/FileViewerModal';
import ApplicationQuestionsManager from './components/ApplicationQuestionsManager';
import GreetingMessageEditor from './components/GreetingMessageEditor';
import FormCustomizationPanel from './components/FormCustomizationPanel';
import ScreeningAutomationPanel from './components/ScreeningAutomationPanel';
import ApplicationUrlDisplay from './components/ApplicationUrlDisplay';
import {
    getQuestionnaireForInterviewModal,
    normalizeInterviewEvaluationResults,
    getCandidateFitScore,
} from './utils/interviewEvalUtils';


// Helper function to convert paragraph to bullet points
const convertToBulletPoints = (text) => {
    if (!text || text === "No analysis available") return text;
    
    // Split by sentence endings but be careful about decimal numbers and abbreviations
    // This regex looks for periods followed by space and capital letter, or end of string
    const sentences = text.split(/\.(?=\s+[A-Z])|\.(?=\s*$)/).filter(sentence => sentence.trim().length > 0);
    
    return sentences.map(sentence => {
        let cleaned = sentence.trim();
        // Add period back if it was removed by the split
        if (!cleaned.endsWith('.') && !cleaned.endsWith('!') && !cleaned.endsWith('?')) {
            cleaned += '.';
        }
        return cleaned;
    });
};

const generateWeightedSummary = (selectedResume, advancedAnalysis) => {
    const overallComment = selectedResume.analysis?.reasoning || "No analysis available";
    // Use filtered_overall_assessment for summary (non-zero weight sections only)
    const overallAssessment = advancedAnalysis?.filtered_overall_assessment || advancedAnalysis?.overall_assessment || {};
    
    // Build strengths and shortfalls
    const strengths = overallAssessment.strengths || [];
    const shortfalls = overallAssessment.Shortfall_Areas || [];
    
    // Build the summary
    let summary = overallComment;
    
    if (strengths.length > 0) {
        summary += ` The candidate demonstrates ${strengths.slice(0, 5).join(" and ")}.`;
    }
    
    if (shortfalls.length > 0) {
        summary += ` However, ${shortfalls.slice(0, 1).join(" and ")} represent areas for development.`;
    }
    
    return summary;
};
const SkillMatrix = ({ skills }) => {
    // Handle both old and new formats
    let matchingSkills = {};
    let missingSkills = {};
    let totalMatches = 0;
    let totalGaps = 0;
    
    // Check if it's the new format
    if (skills?.matching_skills && skills?.missing_job_skills) {
        matchingSkills = skills.matching_skills;
        missingSkills = skills.missing_job_skills;
        
        totalMatches = [
            ...(matchingSkills.matching_hard_skills || []),
            ...(matchingSkills.matching_soft_skills || []),
            ...(matchingSkills.matching_tools_technologies_frameworks || []),
            ...(matchingSkills.matching_languages || [])
        ].length;
        
        totalGaps = [
            ...(missingSkills.missing_hard_skills || []),
            ...(missingSkills.missing_soft_skills || []),
            ...(missingSkills.missing_tools_technologies_frameworks || []),
            ...(missingSkills.missing_languages || [])
        ].length;
    } 
    // Handle old format (matches and gaps arrays)
    else if (skills?.matches && skills?.gaps) {
        totalMatches = skills.matches.length;
        totalGaps = skills.gaps.length;
        
        // Convert old format to new format for display
        matchingSkills = {
            matching_hard_skills: skills.matches,
            matching_soft_skills: [],
            matching_tools_technologies_frameworks: [],
            matching_languages: []
        };
        
        missingSkills = {
            missing_hard_skills: skills.gaps,
            missing_soft_skills: [],
            missing_tools_technologies_frameworks: [],
            missing_languages: []
        };
    }

    // Only show skill matrix if there's actual data
    if (totalMatches === 0 && totalGaps === 0) {
        return null;
    }
    
    return (
        <div className="skill-matrix">
            {totalMatches > 0 && (
            <div className="skill-column matches">
                <h4>✅ Matches ({totalMatches})</h4>

                {matchingSkills.matching_hard_skills?.length > 0 && (
                    <div className="skill-category">
                        <h5>Hard Skills</h5>
                        <ul>
                            {matchingSkills.matching_hard_skills.map((skill, i) =>
                                <li key={`hard-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {matchingSkills.matching_soft_skills?.length > 0 && (
                    <div className="skill-category">
                        <h5>Soft Skills</h5>
                        <ul>
                            {matchingSkills.matching_soft_skills.map((skill, i) =>
                                <li key={`soft-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {matchingSkills.matching_tools_technologies_frameworks?.length > 0 && (
                    <div className="skill-category">
                        <h5>Tools/Technologies/Frameworks</h5>
                        <ul>
                            {matchingSkills.matching_tools_technologies_frameworks.map((skill, i) =>
                                <li key={`tools-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {matchingSkills.matching_languages?.length > 0 && (
                    <div className="skill-category">
                        <h5>Languages</h5>
                        <ul>
                            {matchingSkills.matching_languages.map((skill, i) =>
                                <li key={`lang-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
            )}

            {totalGaps > 0 && (
            <div className="skill-column gaps">
                <h4>🚫 Gaps ({totalGaps})</h4>

                {missingSkills.missing_hard_skills?.length > 0 && (
                    <div className="skill-category">
                        <h5>Hard Skills</h5>
                        <ul>
                            {missingSkills.missing_hard_skills.map((skill, i) =>
                                <li key={`missing-hard-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {missingSkills.missing_soft_skills?.length > 0 && (
                    <div className="skill-category">
                        <h5>Soft Skills</h5>
                        <ul>
                            {missingSkills.missing_soft_skills.map((skill, i) =>
                                <li key={`missing-soft-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {missingSkills.missing_tools_technologies_frameworks?.length > 0 && (
                    <div className="skill-category">
                        <h5>Tools/Technologies/Frameworks</h5>
                        <ul>
                            {missingSkills.missing_tools_technologies_frameworks.map((skill, i) =>
                                <li key={`missing-tools-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}

                {missingSkills.missing_languages?.length > 0 && (
                    <div className="skill-category">
                        <h5>Languages</h5>
                        <ul>
                            {missingSkills.missing_languages.map((skill, i) =>
                                <li key={`missing-lang-${i}`}>{skill}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

const Timeline = ({ timeline }) => {
    // Only show timeline if there's actual data
    if (!timeline || timeline.length === 0) {
        return null;
    }
    
    return (
        <div className="timeline">
            <h4>Timeline & Impact</h4>
            <ul>
                {timeline.map((item, i) => (
                    <li key={`timeline-${i}`}>
                        <strong>{item.period}:</strong> {item.role} - <em>{item.details}</em>
                    </li>
                ))}
            </ul>
        </div>
    );
};


const InterviewHub = ({ resume, onSchedule, onFeedback, onEvaluation }) => {
    const { interview } = resume;

    // A small helper for a consistent "coming soon" alert
    const comingSoon = (feature) => alert(`${feature}: Coming soon!`);

    if (interview) {
        switch (interview.status) {
            case 'scheduled':
            case 'rescheduled':
                return (
                    <div className="interview-hub">
                        <span className="interview-status-text">✅ Interview Scheduled</span>
                        <button className="action-button" onClick={() => onSchedule(resume)}>
                            Reschedule
                        </button>
                        <button className="action-button" onClick={() => comingSoon('Prepare Questions')}>
                            ❓ Prepare Questions
                        </button>
                    </div>
                );
            case 'completed':
                return (
                    <div className="interview-hub">
                       <span className="interview-status-text">⭐ Completed</span>
                       <button className="action-button feedback" onClick={() => onFeedback(resume)}>
                            📝 Log Feedback
                       </button>
                    </div>
                );
            // Add other statuses like 'cancelled' if needed
            default:
                // Fallback for any other status, shows the main schedule button
                break;
        }
    }

    // Default case if no interview object exists or status is not handled above
    return (
        <div className="interview-hub">
            <button 
                className="action-button schedule-interview" 
                onClick={() => onSchedule(resume)}
            >
                <span role="img" aria-label="calendar icon" style={{ marginRight: '8px' }}>📅</span>
                Schedule Interview
            </button>
            {/* Evaluate Interview button - Available for all candidates */}
            <button 
                className="action-button evaluation"
                onClick={() => onEvaluation(resume)}
            >
                🎯 Evaluate Interview
            </button>
        </div>
    );
};
const JobDetailsPage = () => {
    const { jobId } = useParams();
    const location = useLocation();
    const focusScreeningOnOpen = Boolean(location.state?.focusScreening);
    const focusScreeningRef = useRef(focusScreeningOnOpen);
    const [screeningHighlight, setScreeningHighlight] = useState(focusScreeningOnOpen);
    const [jobDetails, setJobDetails] = useState(null);
    const [selectedResumeId, setSelectedResumeId] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isAutoRefresh, setIsAutoRefresh] = useState(false);
    const [scoreInfoModal, setScoreInfoModal] = useState(null);
    // Removed unused overallComment state
    // Set up callback for overall comment extraction
    React.useEffect(() => {
        // Removed setOverallComment reference
        return () => {
            // Removed setOverallComment cleanup
        };
    }, []);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [selectedResumeForFeedback, setSelectedResumeForFeedback] = useState(null);
    const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
    const [currentInterviewResume, setCurrentInterviewResume] = useState(null);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedResumeForSchedule, setSelectedResumeForSchedule] = useState(null);
    const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
    const [currentQuestionnaireResume, setCurrentQuestionnaireResume] = useState(null);
    const [activeTab, setActiveTab] = useState('candidates'); // 'candidates' or 'application-form'
    
    // Check URL hash on mount and when jobId changes
    useEffect(() => {
        if (window.location.hash === '#application-form') {
            setActiveTab('application-form');
        }
    }, [jobId]);

    // Opening from Auto-reject chip: stay at top, soft highlight, no heavy auto-select jump
    useEffect(() => {
        focusScreeningRef.current = Boolean(location.state?.focusScreening);
        setScreeningHighlight(Boolean(location.state?.focusScreening));
        if (!location.state?.focusScreening) return undefined;

        window.scrollTo({ top: 0, behavior: 'smooth' });
        const clearHighlight = window.setTimeout(() => setScreeningHighlight(false), 1800);
        // Drop navigation state so refresh/back doesn't keep re-focusing
        if (window.history.replaceState) {
            window.history.replaceState({ ...(window.history.state || {}), focusScreening: false }, '');
        }
        return () => window.clearTimeout(clearHighlight);
    }, [jobId, location.state]);
    
    // Debug state changes
    useEffect(() => {
        console.log("🔍 Modal state changed - isQuestionnaireModalOpen:", isQuestionnaireModalOpen, "currentQuestionnaireResume:", currentQuestionnaireResume);
    }, [isQuestionnaireModalOpen, currentQuestionnaireResume]);    const detailsRef = useRef(null);
    const [isInterviewEvaluationModalOpen, setIsInterviewEvaluationModalOpen] = useState(false);
    const [selectedResumeForEvaluation, setSelectedResumeForEvaluation] = useState(null);
    const [isGeneratingQuestionnaire, setIsGeneratingQuestionnaire] = useState(false);
    const stableCountRef = useRef(0);
    const lastResumeCountRef = useRef(0);
    const totalResumesRef = useRef(0); // Track the total number of resumes originally uploaded
    const selectedResumeIdRef = useRef(null);
    const hasUserSelectedRef = useRef(false);
    const [shareStatus, setShareStatus] = useState(null); // 'idle' | 'loading' | 'success' | 'error'
    const [shareError, setShareError] = useState(null);
    const [isJDModalOpen, setIsJDModalOpen] = useState(false);
    const [jdContent, setJdContent] = useState('');
    const [jdFilename, setJdFilename] = useState('');
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [resumeContent, setResumeContent] = useState('');
    const [resumeFilename, setResumeFilename] = useState('');
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [analysisContent, setAnalysisContent] = useState('');
    const [analysisFilename, setAnalysisFilename] = useState('');

    // Keep a ref to the latest selection so auto-refresh can't override user clicks
    useEffect(() => {
        selectedResumeIdRef.current = selectedResumeId;
    }, [selectedResumeId]);

    // Reset selection state on job change
    useEffect(() => {
        hasUserSelectedRef.current = false;
        selectedResumeIdRef.current = null;
        setSelectedResumeId(null);
    }, [jobId]);

    const fetchJobDetails = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}`);
            if (response.status === 404) {
                throw new Error('Job not found.');
            }
            await throwIfNotOk(response);
            const data = await response.json();
            
            // Check if resume count is stable
            const currentResumeCount = data.resumes ? data.resumes.length : 0;
            if (currentResumeCount === lastResumeCountRef.current) {
                stableCountRef.current++;
            } else {
                stableCountRef.current = 0;
                lastResumeCountRef.current = currentResumeCount;
            }
            
            // Track the maximum total resumes we've seen.
            // Handles both cases:
            // - Not all resumes are in DB yet (use stored/API expected totals)
            // - User later adds more resumes to an existing job (use current DB count)
            const jobIdStr = String(jobId);
            const storedTotal = localStorage.getItem(`job_${jobIdStr}_total_resumes`);
            if (storedTotal) {
                const storedCount = parseInt(storedTotal, 10);
                if (!isNaN(storedCount) && storedCount > 0) {
                    totalResumesRef.current = Math.max(totalResumesRef.current, storedCount);
                }
            }
            
            // Also check API response for total_resumes or expected_total_resumes
            const apiTotal = data.expected_total_resumes || data.total_resumes;
            if (apiTotal && apiTotal > totalResumesRef.current) {
                totalResumesRef.current = apiTotal;
            }
            
            // Always account for what is currently in DB (important when adding resumes later)
            if (currentResumeCount > 0) {
                totalResumesRef.current = Math.max(totalResumesRef.current, currentResumeCount);
            }
            
            setJobDetails(data);
            
            // Auto-select only once (initial load) and never override a user click.
            // Skip when opening from Auto-reject chip so the page stays light and at the top.
            const alreadySelected = Boolean(selectedResumeIdRef.current);
            if (
                !alreadySelected
                && !hasUserSelectedRef.current
                && !focusScreeningRef.current
                && data.resumes
                && data.resumes.length > 0
                && !isAutoRefresh
            ) {
                const processingResumes = data.resumes.filter(r => !r.analysis && !r.rag_analysis);
                const completedResumes = data.resumes.filter(r => r.analysis || r.rag_analysis);
                
                // Priority 1: Select first processing resume (newly uploaded, still analyzing)
                if (processingResumes.length > 0) {
                    setSelectedResumeId(processingResumes[0].id);
                    console.log(`[JobDetails] Auto-selected processing resume: ${processingResumes[0].filename} (ID: ${processingResumes[0].id})`);
                }
                // Priority 2: Select first completed resume (fallback if all are done)
                else if (completedResumes.length > 0) {
                    setSelectedResumeId(completedResumes[0].id);
                    console.log(`[JobDetails] Auto-selected completed resume: ${completedResumes[0].filename} (ID: ${completedResumes[0].id})`);
                }
                // Priority 3: Fallback to first resume (shouldn't happen)
                else {
                    setSelectedResumeId(data.resumes[0].id);
                }
            }
            
            // Processing only when there are resumes still missing analysis (never for empty 0/0)
            const totalResumes = data.resumes?.length || 0;
            const analyzedResumes = data.resumes?.filter(r => r.analysis || r.rag_analysis)?.length || 0;
            const stillProcessing = totalResumes > 0 && analyzedResumes < totalResumes;
            setIsProcessing(stillProcessing);

            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    useEffect(() => {
        setIsLoading(true);
        fetchJobDetails();
    }, [jobId, fetchJobDetails]);

    // Auto-refresh when processing resumes
    useEffect(() => {
        let interval;
        
        // Continue auto-refresh until we detect processing is complete
        if (jobDetails) {
            interval = setInterval(() => {
                setIsAutoRefresh(true);
                
                // Check current state first before setting processing flags
                const totalResumes = jobDetails.resumes?.length || 0;
                const analyzedResumes = jobDetails.resumes?.filter(r => r.analysis || r.rag_analysis)?.length || 0;
                const stillProcessing = totalResumes > 0 && analyzedResumes < totalResumes;
                
                // Only set processing flags if actually still processing
                if (stillProcessing) {
                    setIsProcessing(true);
                } else {
                    setIsProcessing(false);
                }
                
                // Continue refreshing if:
                // 1. We have resumes but not all are analyzed
                // 2. We're in the first few seconds after redirect (allow time for processing to start)
                
                // Dynamic timeout: 3 minutes per resume (180 * 3s = 540s per resume)
                const dynamicTimeout = Math.max(60, totalResumes * 3); // At least 3 minutes, then 3 minutes per resume
                const condition1 = stableCountRef.current < dynamicTimeout; // Dynamic timeout based on resume count
                const condition3 = analyzedResumes < totalResumes; // Some resumes not analyzed yet
                
                const shouldContinue = stillProcessing && (condition1 || condition3);
                
                if (shouldContinue) {
                    fetchJobDetails();
                } else {
                    clearInterval(interval);
                    setIsProcessing(false);
                    setIsAutoRefresh(false); // Clear auto-refresh flag
                }
            }, 3000); // Poll every 3 seconds
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [jobDetails, fetchJobDetails]);

    const sortedResumes = useMemo(() => {
        if (!jobDetails?.resumes) return [];
        return [...jobDetails.resumes].sort((a, b) => (b.analysis?.fit_score || 0) - (a.analysis?.fit_score || 0));
    }, [jobDetails]);

    const selectedResume = useMemo(() => {
        if (!selectedResumeId) return null;
        return sortedResumes.find(r => r.id === selectedResumeId);
    }, [selectedResumeId, sortedResumes]);

    // Scroll to details only when the user picks a candidate (not on auto-select / page load)
    useEffect(() => {
        if (!selectedResumeId || !detailsRef.current || !hasUserSelectedRef.current) return;
        detailsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [selectedResumeId]);

    const handleRowClick = (resumeId) => {
        hasUserSelectedRef.current = true;
        setSelectedResumeId(resumeId);
    };


    const handleFeedbackSubmit = () => {
        // Refresh job details to show any updates
        window.location.reload();
    };

    const handleOverrideSubmit = (newBucket) => {
        // Update the local state to reflect the override
        if (selectedResumeForFeedback) {
            const updatedResume = {
                ...selectedResumeForFeedback,
                analysis: {
                    ...selectedResumeForFeedback.analysis,
                    bucket: newBucket,
                    original_bucket: selectedResumeForFeedback.analysis.bucket,
                    is_override: true
                }
            };
            setSelectedResumeForFeedback(updatedResume);
            
            // Update the job details
            setJobDetails(prev => ({
                ...prev,
                resumes: prev.resumes.map(r => 
                    r.id === selectedResumeForFeedback.id ? updatedResume : r
                )
            }));
        }
    };

    const handleScoreAdjustmentSubmitted = (adjustmentData) => {
        // Refresh job details to show updated score
        if (jobId) {
            fetchJobDetails();
        }
    };

    // Removed unused function - handleInterviewClick

    const handleCloseInterviewModal = () => {
        setIsInterviewModalOpen(false);
        setCurrentInterviewResume(null);
    };

    const handleInterviewCreated = (interviewId) => {
        console.log("Interview created with ID:", interviewId);
        handleCloseInterviewModal();
    };
    const handleQuestionnaireClick = (resume) => {
        console.log("Questionnaire button clicked for resume:", resume);
        console.log("🔍 Setting currentQuestionnaireResume:", resume);
        setCurrentQuestionnaireResume(resume);
        console.log("🔍 Setting isQuestionnaireModalOpen to true");
        setIsQuestionnaireModalOpen(true);
        console.log("Questionnaire modal should be opening now");
    };

    const handleCloseQuestionnaireModal = () => {
        setIsQuestionnaireModalOpen(false);
        setCurrentQuestionnaireResume(null);
    }
    const handleCloseInterviewEvaluationModal = () => {
        setIsInterviewEvaluationModalOpen(false);
        setSelectedResumeForEvaluation(null);
    };

    const handleQuestionnaireReady = () => {
        console.log('🎯 Questionnaire is ready, turning off loading state');
        setIsGeneratingQuestionnaire(false);
    };

    const handleScheduleInterview = (resume) => {
        setSelectedResumeForSchedule(resume);
        setIsScheduleModalOpen(true);
    };

    const handleCloseScheduleModal = () => {
        setIsScheduleModalOpen(false);
        setSelectedResumeForSchedule(null);
    };

    const handleScheduleSubmit = async (interviewData) => {
        try {
            const response = await apiFetch('/api/google-schedule-interview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(interviewData)
            });

            await throwIfNotOk(response);

            await response.json();
            alert('Available time slots sent to candidate! The candidate will receive an email with available time slots to choose from.');
            
            // Refresh job details to show updated interview status
            fetchJobDetails();
        } catch (error) {
            console.error('Error scheduling interview:', error);
            throw error;
        }
    };
    const handleInterviewEvaluationClick = async (resume) => {
        console.log('🎯 Evaluation button clicked for resume:', resume);
        
        // Get the updated resume data from jobDetails to ensure we have the latest data
        const updatedResume = jobDetails?.resumes?.find(r => r.id === resume.id) || resume;
        console.log('📋 Updated resume has questionnaire:', !!updatedResume.questionnaire);
        console.log('📋 Updated resume evaluation completed:', updatedResume.evaluation_completed);
        
        // Check if evaluation was already completed
        if (updatedResume.evaluation_completed && updatedResume.evaluation_results) {
            console.log('✅ Evaluation already completed, showing results');
            setSelectedResumeForEvaluation(updatedResume);
            setIsInterviewEvaluationModalOpen(true);
            return;
        }
        
        // Treat missing or empty questionnaire the same (DB may store {} or empty sections)
        if (!getQuestionnaireForInterviewModal(updatedResume)) {
            console.log('📋 No usable questionnaire found, showing hardcoded question immediately...');
            
            // Create hardcoded questionnaire with introduction question
            const hardcodedQuestionnaire = {
                introduction: [{
                    id: "hardcoded_intro_question",
                    question: "Please walk us through your professional background, highlighting the key experiences and skills that align with this role. What motivates you to pursue this position, and how do you believe your unique combination of expertise makes you the ideal candidate?",
                    type: "introduction",
                    weightage: "High",
                    difficulty_level: "Medium",
                    section: "introduction",
                    sectionTitle: "Introduction"
                }],
                behavioral: [],
                technical: { theoretical: [], practical: [] },
                situational: []
            };
            
            // Show hardcoded question immediately
            const resumeWithHardcodedQuestion = {
                ...updatedResume,
                questionnaire: hardcodedQuestionnaire
            };
            
            setSelectedResumeForEvaluation(resumeWithHardcodedQuestion);
            setIsInterviewEvaluationModalOpen(true);
            setIsGeneratingQuestionnaire(true);
            
            const POLL_INTERVAL_MS = 2500;
            const POLL_TIMEOUT_MS = 3 * 60 * 1000;

            const pollQuestionnaireStatus = async (resumeId) => {
                const deadline = Date.now() + POLL_TIMEOUT_MS;
                while (Date.now() < deadline) {
                    const res = await apiFetch(`/api/resumes/${resumeId}/questionnaire-status`);
                    const body = await res.json().catch(() => ({}));
                    if (body.ready && body.questionnaire) return body.questionnaire;
                    if (body.error && res.status >= 400) throw new Error(body.error || 'Poll failed');
                    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
                }
                throw new Error('Questionnaire generation timed out. Please try again.');
            };

            const generateQuestionnaireWithRetry = async (maxRetries = 3) => {
                const requestKey = `questionnaire_${updatedResume.id}`;
                if (window[requestKey]) {
                    console.log('⏳ Questionnaire generation already in progress, waiting...');
                    return window[requestKey];
                }

                const makeRequest = async (attempt = 1) => {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 20000);

                    try {
                        console.log(`🔄 Generating questionnaire (attempt ${attempt}/${maxRetries})...`);
                        const response = await apiFetch('/api/generate-questionnaire', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                resume_id: updatedResume.id,
                                job_description: jobDetails?.description || "",
                                candidate_name: updatedResume.candidate_name,
                                fit_score: updatedResume.analysis?.fit_score
                            }),
                            signal: controller.signal
                        });

                        clearTimeout(timeoutId);
                        const data = await response.json().catch(() => ({}));

                        if (response.status === 202 && data.status === 'processing') {
                            console.log('🔄 Questionnaire generation started in background, polling...');
                            const questionnaire = await pollQuestionnaireStatus(updatedResume.id);
                            return { questionnaire };
                        }

                        if (!response.ok) {
                            throw new Error(
                                getUserFacingErrorMessage(
                                    response.status,
                                    { detail: data },
                                    data.error || `HTTP ${response.status}`
                                )
                            );
                        }
                        if (data.questionnaire) return data;
                        throw new Error(data.error || 'No questionnaire in response');
                    } catch (error) {
                        clearTimeout(timeoutId);
                        if (error.name === 'AbortError') throw new Error('Request timeout - please try again');
                        if (error.message.includes('HTTP 4')) throw error;
                        if (attempt < maxRetries) {
                            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
                            console.log(`⏳ Retrying in ${delay}ms... (${error.message})`);
                            await new Promise(resolve => setTimeout(resolve, delay));
                            return makeRequest(attempt + 1);
                        }
                        throw error;
                    }
                };

                window[requestKey] = makeRequest();
                try {
                    const data = await window[requestKey];
                    delete window[requestKey];
                    return data;
                } catch (error) {
                    delete window[requestKey];
                    throw error;
                }
            };

            try {
                const data = await generateQuestionnaireWithRetry();

                // Merge hardcoded question with generated questions
                const mergedQuestionnaire = {
                    ...data.questionnaire,
                    introduction: [
                        ...hardcodedQuestionnaire.introduction // Only keep hardcoded question, don't add generated intro questions
                    ]
                };

                // Save questionnaire to database immediately
                try {
                    console.log('💾 Saving merged questionnaire to database...');
                    const saveResponse = await apiFetch('/api/save-questionnaire', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            resume_id: updatedResume.id,
                            questionnaire: mergedQuestionnaire
                        })
                    });

                    if (!saveResponse.ok) {
                        const errorText = await saveResponse.text();
                        console.error('❌ Failed to save questionnaire:', saveResponse.status, errorText);
                        throw new Error('Failed to save questionnaire to database');
                    }

                    const saveResult = await saveResponse.json();
                    console.log('✅ Questionnaire saved to database:', saveResult);
                    
                    // Wait a bit for database commit to complete
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    // Refresh job details to get the saved questionnaire from database
                    await fetchJobDetails();
                    
                } catch (saveError) {
                    console.error('❌ Error saving questionnaire:', saveError);
                    // Don't show alert - questionnaire is still in memory and will work
                    console.warn('⚠️ Questionnaire generated but failed to save. It may not persist after refresh.');
                }

                // Update the resume with the merged questionnaire
                const updatedResumeWithMergedQuestionnaire = {
                    ...updatedResume,
                    questionnaire: mergedQuestionnaire
                };

                // Update local state
                setJobDetails(prev => ({
                    ...prev,
                    resumes: prev.resumes.map(r =>
                        r.id === updatedResume.id
                            ? updatedResumeWithMergedQuestionnaire
                            : r
                    )
                }));

                // Update the modal with the merged questionnaire
                setSelectedResumeForEvaluation(updatedResumeWithMergedQuestionnaire);
                
                // Turn off loading state now that all questions are ready
                setIsGeneratingQuestionnaire(false);

            } catch (error) {
                console.error('❌ Error generating additional questions after retries:', error);
                // Silently retry one more time after a longer delay
                console.log('🔄 Attempting final retry after 5 seconds...');
                await new Promise(resolve => setTimeout(resolve, 5000));
                
                try {
                    const data = await generateQuestionnaireWithRetry(1); // Single final attempt
                    
                    const mergedQuestionnaire = {
                        ...data.questionnaire,
                        introduction: [...hardcodedQuestionnaire.introduction]
                    };
                    
                    // Save and update (same as above)
                    try {
                        const saveResponse = await apiFetch('/api/save-questionnaire', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                resume_id: updatedResume.id,
                                questionnaire: mergedQuestionnaire
                            })
                        });
                        if (saveResponse.ok) {
                            await fetchJobDetails();
                        }
                    } catch (saveErr) {
                        console.warn('⚠️ Save failed on retry:', saveErr);
                    }
                    
                    const updatedResumeWithMergedQuestionnaire = {
                        ...updatedResume,
                        questionnaire: mergedQuestionnaire
                    };
                    
                    setJobDetails(prev => ({
                        ...prev,
                        resumes: prev.resumes.map(r =>
                            r.id === updatedResume.id
                                ? updatedResumeWithMergedQuestionnaire
                                : r
                        )
                    }));
                    
                    setSelectedResumeForEvaluation(updatedResumeWithMergedQuestionnaire);
                    setIsGeneratingQuestionnaire(false);
                    
                } catch (finalError) {
                    console.error('❌ Final retry also failed:', finalError);
                    // Only show error if ALL retries failed - but still allow using introduction question
                    setIsGeneratingQuestionnaire(false);
                    // Don't show alert - user can still use introduction question
                    // The modal is already open with the hardcoded question
                }
            }
            return;
        }
        
        console.log('✅ Questionnaire found, opening evaluation modal');
        
        // Refresh job details to ensure we have the latest questionnaire from database
        try {
            const refreshResponse = await apiFetch(`/api/jobs/${jobId}`);
            if (refreshResponse.ok) {
                const refreshedData = await refreshResponse.json();
                const refreshedResume = refreshedData?.resumes?.find(r => r.id === resume.id);
                if (refreshedResume && getQuestionnaireForInterviewModal(refreshedResume)) {
                    // Update jobDetails state
                    setJobDetails(refreshedData);
                    // Use the refreshed resume with saved questionnaire
                    setSelectedResumeForEvaluation(refreshedResume);
                    setIsInterviewEvaluationModalOpen(true);
                    return;
                }
            }
        } catch (refreshError) {
            console.warn('⚠️ Failed to refresh job details, using cached data:', refreshError);
        }
        
        // Fallback: use the updated resume we already have
        setSelectedResumeForEvaluation(updatedResume);
        setIsInterviewEvaluationModalOpen(true);
    };

    const handleQuestionnaireGenerated = async (questionnaireData) => {
        alert('🚀 handleQuestionnaireGenerated called!');
        console.log('🚀 handleQuestionnaireGenerated called!');
        console.log('📋 Questionnaire data:', questionnaireData);
        console.log('📋 Current resume ID:', currentQuestionnaireResume?.id);
        console.log('📋 Current resume:', currentQuestionnaireResume);
        
        if (!currentQuestionnaireResume) {
            console.error('❌ currentQuestionnaireResume is null/undefined!');
            alert('Error: Resume data not found. Please try again.');
            return;
        }
        
        try {
            console.log('📋 Saving questionnaire to database...');
            
            // Save to database first
            const response = await apiFetch('/api/save-questionnaire', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    resume_id: currentQuestionnaireResume.id,
                    questionnaire: questionnaireData
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                let parsed = {};
                try {
                    parsed = JSON.parse(errorText);
                } catch {
                    parsed = { detail: errorText };
                }
                console.error('❌ Save questionnaire failed:', response.status, errorText);
                throw new Error(
                    getUserFacingErrorMessage(response.status, parsed, 'Failed to save questionnaire to database')
                );
            }

            const saveResult = await response.json();
            console.log('✅ Questionnaire saved to database:', saveResult);
            console.log('🔍 Questionnaire structure:', JSON.stringify(questionnaireData, null, 2));
            console.log('🔍 Questionnaire sections:', Object.keys(questionnaireData));
            console.log('🔍 Introduction questions:', questionnaireData.introduction);
            console.log('🔍 Behavioral questions:', questionnaireData.behavioral);
            console.log('🔍 Technical questions:', questionnaireData.technical);
            
            // Update local state
            setJobDetails(prevJobDetails => {
                if (!prevJobDetails) return prevJobDetails;
                return {
                    ...prevJobDetails,
                    resumes: prevJobDetails.resumes.map(resume => 
                        resume.id === currentQuestionnaireResume.id 
                            ? { ...resume, questionnaire: questionnaireData }
                            : resume
                    )
                };
            });
            
            console.log('✅ Questionnaire saved to local state');
            handleCloseQuestionnaireModal();
        } catch (error) {
            console.error('Error saving questionnaire:', error);
            alert('Failed to save questionnaire. Please try again.');
        }
    };

    const getScoreClass = (score) => {
        if (score >= 90) return "high";
        if (score >= 80) return "medium-high";
        if (score >= 65) return "medium";
        return "low";
    };

    const handleShareLinkedIn = async () => {
        setShareStatus('loading');
        setShareError(null);
        try {
            const res = await apiFetch('/api/linkedin/share-job', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ job_id: parseInt(jobId) })
            });
            const data = await res.json();
            if (data.success) {
                setShareStatus('success');
            } else if (data.error === 'linkedin_not_connected') {
                window.location.href = `${getBackendUrl()}/api/auth/linkedin/init`;
            } else {
                setShareStatus('error');
                setShareError(data.error || 'Unknown error');
            }
        } catch (e) {
            setShareStatus('error');
            setShareError(e.message);
        }
    };

    const handleViewJD = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/job-description`, {
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
                console.error('JD fetch error:', response.status, errorData);
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        errorData,
                        errorData.error || `Failed to fetch job description (${response.status})`
                    )
                );
            }
            const data = await response.json();
            setJdContent(data.content);
            setJdFilename(data.filename);
            setIsJDModalOpen(true);
        } catch (error) {
            console.error('Error fetching job description:', error);
            alert(`Failed to load job description: ${error.message}. Please try again.`);
        }
    };

    const handleCloseJDModal = () => {
        setIsJDModalOpen(false);
        setJdContent('');
        setJdFilename('');
    };

    const handleViewResume = async (resumeId) => {
        try {
            const response = await apiFetch(`/api/resumes/${resumeId}/file`, {
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
                console.error('Resume fetch error:', response.status, errorData);
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        errorData,
                        errorData.error || `Failed to fetch resume file (${response.status})`
                    )
                );
            }
            const data = await response.json();
            setResumeContent(data.content);
            setResumeFilename(data.filename);
            setIsResumeModalOpen(true);
        } catch (error) {
            console.error('Error fetching resume file:', error);
            alert(`Failed to load resume: ${error.message}. Please try again.`);
        }
    };

    const handleCloseResumeModal = () => {
        setIsResumeModalOpen(false);
        setResumeContent('');
        setResumeFilename('');
    };

    const handleViewCompleteAnalysis = async (resumeId) => {
        try {
            const response = await apiFetch(`/api/resumes/${resumeId}/complete-analysis`, {
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
                console.error('Complete analysis fetch error:', response.status, errorData);
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        errorData,
                        errorData.error || `Failed to fetch complete analysis (${response.status})`
                    )
                );
            }
            const data = await response.json();
            setAnalysisContent(data.content);
            setAnalysisFilename(data.filename);
            setIsAnalysisModalOpen(true);
        } catch (error) {
            console.error('Error fetching complete analysis:', error);
            alert(`Failed to load complete analysis: ${error.message}. Please try again.`);
        }
    };

    const handleCloseAnalysisModal = () => {
        setIsAnalysisModalOpen(false);
        setAnalysisContent('');
        setAnalysisFilename('');
    };
    if (isLoading) {
        return (
            <div className="job-details-container job-details-enter">
                <p className="job-details-loading">Loading job details…</p>
            </div>
        );
    }
    if (error) return <div className="job-details-container message error">Error: {error}</div>;
    if (!jobDetails) return <div className="job-details-container"><p>Job not found.</p></div>;
    
    return (
        <div className="job-details-container job-details-enter">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <Link to="/jobs" className="back-link">← Back to All Jobs</Link>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button 
                        onClick={handleViewJD} 
                        className="btn jd-view-btn"
                        style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#fff',
                            background: '#6c5ce7',
                            border: 'none',
                            borderRadius: '50px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        📄 View/Download JD
                    </button>
                    <button 
                        onClick={handleShareLinkedIn} 
                        disabled={shareStatus === 'loading'}
                        className="btn linkedin-share-btn"
                        style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#fff',
                            background: '#0077b5',
                            border: 'none',
                            borderRadius: '50px',
                            cursor: shareStatus === 'loading' ? 'not-allowed' : 'pointer',
                            opacity: shareStatus === 'loading' ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {shareStatus === 'loading' ? 'Sharing…' : 'Share on LinkedIn'}
                    </button>
                </div>
            </div>
            {shareStatus === 'success' && (
                <div style={{ 
                    background: 'rgba(0, 119, 181, 0.1)', 
                    color: '#0077b5', 
                    padding: '0.75rem 1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1rem',
                    border: '1px solid rgba(0, 119, 181, 0.3)'
                }}>
                    ✓ Shared to your LinkedIn feed.
                </div>
            )}
            {shareStatus === 'error' && (
                <div style={{ 
                    background: 'rgba(255, 107, 107, 0.1)', 
                    color: '#ff6b6b', 
                    padding: '0.75rem 1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1rem',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                }}>
                    Could not share: {shareError || 'Unknown error'}
                </div>
            )}
            
            {/* AI screening — available for every job (upload JD or create posting) */}
            <div
                className={`glass-container${screeningHighlight ? ' screening-focus-highlight' : ''}`}
                style={{ marginBottom: '1.25rem' }}
            >
                <ScreeningAutomationPanel jobId={jobId} className="embedded" />
            </div>

            {/* Tab Navigation */}
            <div className="job-tabs">
                <button 
                    className={`job-tab ${activeTab === 'candidates' ? 'active' : ''}`}
                    onClick={() => {
                        console.log('[TAB] Switching to candidates tab');
                        setActiveTab('candidates');
                    }}
                >
                    👥 Candidates {jobDetails?.resumes?.length > 0 && `(${jobDetails.resumes.length})`}
                </button>
                {/* Only show Application Form tab if job has application_url or was created via new flow (no resumes) */}
                {(jobDetails?.application_url || (jobDetails?.resumes?.length === 0 && jobDetails?.posting_source === 'manual')) && (
                    <button 
                        className={`job-tab ${activeTab === 'application-form' ? 'active' : ''}`}
                        onClick={() => {
                            console.log('[TAB] Switching to application-form tab');
                            setActiveTab('application-form');
                        }}
                    >
                        📝 Application Form
                    </button>
                )}
            </div>
            
            {/* Tab Content */}
            {activeTab === 'candidates' && (
                <>
            <div className="glass-container job-summary-card">
                <h2>Candidate Overview</h2>
                <div className="candidate-summary-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Candidate</th>
                                <th>Fit Score</th>
                                <th>Bucket</th>
                                <th>Key Skill Hits</th>
                                <th>Gaps / Flags</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedResumes.map((resume, index) => (
                                <tr 
                                    key={resume.id} 
                                    onClick={() => handleRowClick(resume.id)}
                                    className={selectedResumeId === resume.id ? 'selected' : ''}
                                >
                                    <td><strong>{index + 1}</strong></td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span>{resume.candidate_name || resume.filename}</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewResume(resume.id);
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '0.25rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#6c5ce7',
                                                    fontSize: '1rem'
                                                }}
                                                title="View/Download Resume"
                                            >
                                                📄
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        {resume.analysis?.fit_score ? (
                                            <span className={`score-badge ${getScoreClass(resume.analysis.fit_score)}`}>
                                                {resume.analysis.fit_score}
                                            </span>
                                        ) : (
                                            <span className="score-badge processing" style={{
                                                background: 'linear-gradient(135deg, #6c5ce7 0%, #8a2be2 100%)',
                                                color: '#fff',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '12px',
                                                fontSize: '0.85rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <span className="spinner-small" style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    border: '2px solid rgba(255,255,255,0.3)',
                                                    borderTop: '2px solid #fff',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    display: 'inline-block'
                                                }}></span>
                                                Processing...
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {resume.analysis?.bucket ? (
                                            resume.analysis.bucket
                                        ) : (
                                            <span style={{ color: '#888', fontStyle: 'italic' }}>Processing...</span>
                                        )}
                                    </td>
                                    <td>{[
                                        ...(resume.analysis?.skill_matrix?.matching_skills?.matching_hard_skills || []),
                                        ...(resume.analysis?.skill_matrix?.matching_skills?.matching_soft_skills || []),
                                        ...(resume.analysis?.skill_matrix?.matching_skills?.matching_tools_technologies_frameworks || []),
                                        ...(resume.analysis?.skill_matrix?.matching_skills?.matching_languages || [])
                                    ].join(', ') || resume.analysis?.skill_matrix?.matches?.join(', ') || 'N/A'}</td>
                                    <td>{[
                                        ...(resume.analysis?.skill_matrix?.missing_job_skills?.missing_hard_skills || []),
                                        ...(resume.analysis?.skill_matrix?.missing_job_skills?.missing_soft_skills || []),
                                        ...(resume.analysis?.skill_matrix?.missing_job_skills?.missing_tools_technologies_frameworks || []),
                                        ...(resume.analysis?.skill_matrix?.missing_job_skills?.missing_languages || [])
                                    ].join(', ') || resume.analysis?.skill_matrix?.gaps?.join(', ') || 'None'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedResume && (
                <div className="detailed-analysis-section" ref={detailsRef}>
                    {/* CRITICAL FIX: Show processing state for resumes without analysis */}
                    {!selectedResume.analysis && !selectedResume.rag_analysis ? (
                        <div className="glass-container detailed-resume-card">
                            <div className="processing-resume-indicator" style={{
                                padding: '3rem',
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, rgba(108, 92, 231, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
                                borderRadius: '16px',
                                border: '2px dashed rgba(108, 92, 231, 0.3)'
                            }}>
                                <div className="talentvibe-spinner" style={{ margin: '0 auto 2rem' }}></div>
                                <h3 style={{ color: '#6c5ce7', marginBottom: '1rem' }}>
                                    Processing Resume: {selectedResume.filename || selectedResume.candidate_name || 'Unknown'}
                                </h3>
                                <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                                    This resume is being analyzed in the background.
                                </p>
                                <p style={{ color: '#888', fontSize: '0.9rem' }}>
                                    Analysis results will appear here automatically when complete.
                                </p>
                                <p style={{ color: '#aaa', fontSize: '0.8rem', marginTop: '1rem', fontStyle: 'italic' }}>
                                    The page will refresh automatically every 3 seconds...
                                </p>
                            </div>
                        </div>
                    ) : (
                    <div key={selectedResume.id} className="glass-container detailed-resume-card">
                        <div className="detailed-resume-header">
                            <div>
                                <div className="candidate-bucket-tag book-the-call">
                                    🔥 BOOK-THE-CALL
                                </div>
                                <h3 className="candidate-name">{selectedResume.candidate_name || 'Candidate Details'}</h3>
                            </div>
                            {(() => {
                                // Get the best available score: calibrated_score > fit_score (new) > fit_score (old)
                                let displayScore = null;
                                let scoreLabel = 'SCORE';
                                
                                // Try to get from new scoring system first
                                if (selectedResume?.rag_analysis) {
                                    try {
                                        const ragAnalysis = typeof selectedResume.rag_analysis === 'string' 
                                            ? JSON.parse(selectedResume.rag_analysis) 
                                            : selectedResume.rag_analysis;
                                        
                                        if (ragAnalysis?.evaluation_result) {
                                            const evalResult = ragAnalysis.evaluation_result;
                                            if (evalResult.calibrated_score !== undefined && evalResult.calibrated_score !== null) {
                                                displayScore = evalResult.calibrated_score;
                                                scoreLabel = 'CALIBRATED SCORE';
                                            } else if (evalResult.fit_score !== undefined && evalResult.fit_score !== null) {
                                                displayScore = evalResult.fit_score;
                                                scoreLabel = 'FIT SCORE';
                                            } else if (evalResult.overall_score !== undefined && evalResult.overall_score !== null) {
                                                displayScore = evalResult.overall_score;
                                                scoreLabel = 'OVERALL SCORE';
                                            }
                                        }
                                    } catch (e) {
                                        // Fallback to legacy
                                    }
                                }
                                
                                // Fallback to legacy fit_score
                                if (displayScore === null && selectedResume?.analysis?.fit_score !== undefined) {
                                    displayScore = selectedResume.analysis.fit_score;
                                    scoreLabel = 'FIT SCORE';
                                }
                                
                                return (
                                    <>
                                        <div className="score-circle-container-header">
                                            <div className="score-circle">
                                                <span className="score-number">{displayScore !== null ? Math.round(displayScore) : "N/A"}</span>
                                                <span className="score-label">/ 100</span>
                                            </div>
                                            <span 
                                                className="info-icon" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setScoreInfoModal(scoreLabel);
                                                }}
                                                title="Click for more information"
                                                style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    right: '10px',
                                                    cursor: 'pointer',
                                                    color: 'var(--primary-color)',
                                                    fontSize: '0.9rem',
                                                    opacity: 0.7,
                                                    transition: 'opacity 0.2s',
                                                    zIndex: 10,
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '50%',
                                                    background: 'rgba(0, 170, 255, 0.1)',
                                                    border: '1px solid rgba(0, 170, 255, 0.3)'
                                                }}
                                                onMouseEnter={(e) => e.target.style.opacity = '1'}
                                                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                                            >
                                                ⓘ
                                            </span>
                                        </div>
                                        <div className="header-actions">
                                            <div className="actions-container">
                                                <InterviewHub 
                                                    resume={selectedResume} 
                                                    onSchedule={handleScheduleInterview} 
                                                    onFeedback={() => alert('Feedback form coming soon!')}
                                                    onEvaluation={handleInterviewEvaluationClick}
                                                />
                                                <button 
                                                    onClick={() => handleViewResume(selectedResume.id)} 
                                                    className="btn resume-view-btn"
                                                >
                                                    📄 View/Download Resume
                                                </button>
                                                <button 
                                                    onClick={() => handleViewCompleteAnalysis(selectedResume.id)} 
                                                    className="btn analysis-view-btn"
                                                >
                                                    📊 View/Download Complete Analysis
                                                </button>
                                                <div className="score-badge large full-width">
                                                    {scoreLabel}: {displayScore !== null ? displayScore.toFixed(1) : 'N/A'} / 100
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        
                        {/* Unified Candidate Assessment - Right below FIT SCORE */}
                        {(() => {
                            // Extract combined_summary from multiple possible locations
                            let parsedRagAnalysis = selectedResume?.rag_analysis;
                            if (typeof parsedRagAnalysis === 'string') {
                                try {
                                    parsedRagAnalysis = JSON.parse(parsedRagAnalysis);
                                } catch (e) {
                                    // Ignore parse errors
                                }
                            }
                            
                            const summaryPerspectives = selectedResume?.analysis?.summary_perspectives 
                                || parsedRagAnalysis?.summary_perspectives 
                                || selectedResume?.rag_analysis?.summary_perspectives;
                            
                            const combinedSummary = summaryPerspectives?.combined_summary;
                            
                            if (combinedSummary) {
                                // Render markdown
                                let processedText = combinedSummary;
                                
                                // Preprocess markdown (same as AdvancedAnalysis)
                                processedText = processedText.replace(/^(\s*)\*\s{2,}/gm, '$1* ');
                                processedText = processedText.replace(/^(\s*)-\s{2,}(?!-)/gm, '$1- ');
                                processedText = processedText.replace(/([^\n])\s+([*-])\s{2,}(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
                                processedText = processedText.replace(/([^\n])\s+([*-])\s+(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
                                processedText = processedText.replace(/(\n|\.)\s*\*\s{2,}/g, '$1\n* ');
                                processedText = processedText.replace(/(\n|\.)\s*-\s{2,}(?!-)/g, '$1\n- ');
                                processedText = processedText.replace(/(\n|\.)\s*\*\s+/g, '$1\n* ');
                                processedText = processedText.replace(/(\n|\.)\s*-\s+(?!-)/g, '$1\n- ');
                                processedText = processedText.replace(/^(\s*)\*(?![*\s])(.+)/gm, '$1* $2');
                                processedText = processedText.replace(/^(\s*)-(?![-\s])(.+)/gm, '$1- $2');
                                processedText = processedText.replace(/^(\s*)[*-]\s+(\d+\.\s)/gm, '$1$2');
                                processedText = processedText.replace(/^(\s*)(\d+)\.\s{2,}/gm, '$1$2. ');
                                processedText = processedText.replace(/([^\n])\s+(\*\*\d+\.\s[^*]+?\*\*)/g, '$1\n* $2');
                                processedText = processedText.replace(/^(\s*)(\*\*\d+\.\s[^*]+?\*\*)/gm, '$1* $2');
                                
                                // Configure marked
                                marked.setOptions({
                                    breaks: true,
                                    gfm: true,
                                    headerIds: false,
                                    mangle: false,
                                    pedantic: false,
                                    smartLists: true,
                                    smartypants: false
                                });
                                
                                let rawMarkup;
                                try {
                                    rawMarkup = marked.parse(processedText);
                                } catch (e) {
                                    rawMarkup = marked(processedText);
                                }
                                
                                const cleanMarkup = DOMPurify.sanitize(rawMarkup, {
                                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a'],
                                    ALLOWED_ATTR: ['href', 'target', 'rel']
                                });
                                
                                return (
                                    <div className="unified-candidate-assessment-section">
                                        <h3 className="analysis-subtitle">📋 Unified Candidate Assessment</h3>
                                        <div className="unified-assessment-content">
                                            <div 
                                                className="markdown-content unified-candidate-assessment"
                                                dangerouslySetInnerHTML={{ __html: cleanMarkup }}
                                            />
                                        </div>
                                    </div>
                                );
                            }
                            
                            // Fallback to reasoning if no combined_summary
                            const reasoningText = generateWeightedSummary(selectedResume, selectedResume?.analysis?.advanced_analysis);
                            const bulletPoints = convertToBulletPoints(reasoningText);
                            
                            if (typeof bulletPoints === 'string') {
                                return <p className="reasoning">{bulletPoints}</p>;
                            }
                            
                            return (
                                <div className="reasoning">
                                    <ul className="reasoning-list">
                                        {bulletPoints.map((point, index) => (
                                            <li key={index} className="reasoning-point">{point}</li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })()}

                        {/* Enhanced Scoring Display - New Enterprise Features */}
                        <EnhancedScoringDisplay 
                            analysis={selectedResume?.analysis}
                            ragAnalysis={selectedResume?.rag_analysis}
                        />

                        {/* Score Adjustment Component - Human-in-the-Loop */}
                        {(() => {
                            // Get the best available score: calibrated_score > fit_score (new) > fit_score (old)
                            let scoreToUse = null;
                            let scoreSource = 'legacy';
                            
                            // Try to get from new scoring system first
                            if (selectedResume?.rag_analysis) {
                                try {
                                    const ragAnalysis = typeof selectedResume.rag_analysis === 'string' 
                                        ? JSON.parse(selectedResume.rag_analysis) 
                                        : selectedResume.rag_analysis;
                                    
                                    if (ragAnalysis?.evaluation_result) {
                                        const evalResult = ragAnalysis.evaluation_result;
                                        if (evalResult.calibrated_score !== undefined && evalResult.calibrated_score !== null) {
                                            scoreToUse = evalResult.calibrated_score;
                                            scoreSource = 'calibrated';
                                        } else if (evalResult.fit_score !== undefined && evalResult.fit_score !== null) {
                                            scoreToUse = evalResult.fit_score;
                                            scoreSource = 'new_fit';
                                        } else if (evalResult.overall_score !== undefined && evalResult.overall_score !== null) {
                                            scoreToUse = evalResult.overall_score;
                                            scoreSource = 'overall';
                                        }
                                    }
                                } catch (e) {
                                    // Fallback to legacy
                                }
                            }
                            
                            // Fallback to legacy fit_score
                            if (scoreToUse === null && selectedResume?.analysis?.fit_score !== undefined) {
                                scoreToUse = selectedResume.analysis.fit_score;
                                scoreSource = 'legacy';
                            }
                            
                            return scoreToUse !== null && selectedResume?.id ? (
                                <div className="score-adjustment-section">
                                    <ScoreAdjustment
                                        resumeId={selectedResume.id}
                                        originalScore={scoreToUse}
                                        dimension="overall"
                                        onAdjustmentSubmitted={handleScoreAdjustmentSubmitted}
                                    />
                                </div>
                            ) : null;
                        })()}

                        {selectedResume.analysis?.skill_matrix && <SkillMatrix skills={selectedResume.analysis.skill_matrix} />}
                        {selectedResume.analysis?.timeline && <Timeline timeline={selectedResume.analysis.timeline} />}
                        <AdvancedAnalysis 
                            advancedAnalysis={{
                                ...(selectedResume?.analysis?.advanced_analysis || {}),
                                // Include summary_perspectives from top-level analysis if it exists
                                ...(selectedResume?.analysis?.summary_perspectives ? { summary_perspectives: selectedResume.analysis.summary_perspectives } : {})
                            }} 
                            ragAnalysis={selectedResume?.rag_analysis || null}
                            agentMetadata={selectedResume?.agent_metadata || null}
                            resumeId={selectedResume?.id || null}
                        />
                    </div>
                    )}
                </div>
            )}

            {/* Score Information Modal */}
            {scoreInfoModal && (
                <div className="info-modal-overlay" onClick={() => setScoreInfoModal(null)}>
                    <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="info-modal-header">
                            <h3>{scoreInfoModal}</h3>
                            <button className="info-modal-close" onClick={() => setScoreInfoModal(null)}>×</button>
                        </div>
                        <div className="info-modal-body">
                            {scoreInfoModal === 'CALIBRATED SCORE' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Calibrated Score is a normalized score that enables fair comparison across different roles and time periods. It adjusts for role complexity and market conditions.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li>Takes the raw overall score (combination of capability and fit)</li>
                                        <li>Normalizes based on <strong>role archetype</strong> (e.g., AI Product Engineer, Software Engineer)</li>
                                        <li>Adjusts for <strong>benchmark percentile</strong> (how candidate compares to similar roles)</li>
                                        <li>Accounts for market conditions and role difficulty</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>This score allows you to compare candidates across different roles fairly. A 75 calibrated score for a Senior Engineer means the same thing as a 75 for a Product Manager - both are at the same relative performance level for their respective roles.</p>
                                </>
                            )}
                            {scoreInfoModal === 'FIT SCORE' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Fit Score measures how well the candidate aligns with the role and team environment, beyond just technical skills.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li><strong>Role Alignment (30%)</strong>: Job title and responsibility match</li>
                                        <li><strong>Domain Relevance (25%)</strong>: Industry/domain match</li>
                                        <li><strong>Seniority Match (20%)</strong>: Level appropriateness for the role</li>
                                        <li><strong>Communication Signals (15%)</strong>: Resume clarity, communication indicators</li>
                                        <li><strong>Leadership Indicators (10%)</strong>: Leadership experience if role requires it</li>
                                    </ul>
                                    <h4>Score Interpretation:</h4>
                                    <ul>
                                        <li><strong>80-100</strong>: Excellent fit - strong role/team alignment</li>
                                        <li><strong>60-79</strong>: Good fit - acceptable alignment with some considerations</li>
                                        <li><strong>&lt;60</strong>: Poor fit - may struggle with role or team dynamics</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>A high fit score indicates the candidate is likely to integrate well with the team, understand the role expectations, and stay long-term. Low fit scores may indicate retention risk or cultural misalignment.</p>
                                </>
                            )}
                            {scoreInfoModal === 'OVERALL SCORE' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Overall Score is the combined assessment from both capability and fit dimensions, providing a single comprehensive score.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li>Combines <strong>Capability Score</strong> (60% weight) and <strong>Fit Score</strong> (40% weight) by default</li>
                                        <li>Weights can be adjusted based on role requirements</li>
                                        <li>Formula: Overall Score = (Capability × 0.60) + (Fit × 0.40)</li>
                                    </ul>
                                    <h4>Score Interpretation:</h4>
                                    <ul>
                                        <li><strong>80-100</strong>: Strong candidate - high capability and good fit</li>
                                        <li><strong>60-79</strong>: Moderate candidate - acceptable overall assessment</li>
                                        <li><strong>&lt;60</strong>: Weak candidate - significant gaps in capability or fit</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>This score provides a balanced view considering both technical ability and role/team fit. It helps prioritize candidates who are both capable and likely to succeed in the role.</p>
                                </>
                            )}
                            {scoreInfoModal !== 'CALIBRATED SCORE' && scoreInfoModal !== 'FIT SCORE' && scoreInfoModal !== 'OVERALL SCORE' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>This is the primary assessment score for the candidate, representing their overall match for the role.</p>
                                    <h4>Score Interpretation:</h4>
                                    <ul>
                                        <li><strong>80-100</strong>: Excellent match - strong candidate</li>
                                        <li><strong>60-79</strong>: Good match - acceptable candidate</li>
                                        <li><strong>&lt;60</strong>: Weak match - significant gaps</li>
                                    </ul>
                                    <p>Higher scores indicate better match for the role. This score combines multiple dimensions of assessment to provide a comprehensive evaluation.</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                resume={selectedResumeForFeedback}
                onSubmitFeedback={handleFeedbackSubmit}
                onSubmitOverride={handleOverrideSubmit}
            />
            <InterviewModal
                isOpen={isInterviewModalOpen}
                onClose={handleCloseInterviewModal}
                resume={currentInterviewResume}
                jobId={jobId}
                onInterviewCreated={handleInterviewCreated}
            />

            {console.log("🔍 Modal render check - isQuestionnaireModalOpen:", isQuestionnaireModalOpen, "currentQuestionnaireResume:", currentQuestionnaireResume)}
            {isQuestionnaireModalOpen && currentQuestionnaireResume && (
                <QuestionnaireModal
                    isOpen={isQuestionnaireModalOpen}
                    onClose={handleCloseQuestionnaireModal}
                    resume={currentQuestionnaireResume}
                    jobDescription={jobDetails?.description || ""}
                    onGenerateQuestionnaire={handleQuestionnaireGenerated}
                />
            )}

            {isInterviewEvaluationModalOpen && selectedResumeForEvaluation && (
                <InterviewEvaluationModal
                    isOpen={isInterviewEvaluationModalOpen}
                    onClose={handleCloseInterviewEvaluationModal}
                    questionnaire={getQuestionnaireForInterviewModal(selectedResumeForEvaluation)}
                    evaluationCompleted={Boolean(
                        selectedResumeForEvaluation?.evaluation_completed ||
                            normalizeInterviewEvaluationResults(selectedResumeForEvaluation)
                    )}
                    evaluationResults={normalizeInterviewEvaluationResults(selectedResumeForEvaluation)}
                    evaluationPayload={selectedResumeForEvaluation?.evaluation_payload}
                    candidateName={resolveCandidateContact(selectedResumeForEvaluation).name}
                    fitScore={getCandidateFitScore(selectedResumeForEvaluation)}
                    jobTitle={selectedResumeForEvaluation?.analysis?.job_title || "Position"}
                    resumeId={selectedResumeForEvaluation?.id}
                    jobDescription={jobDetails?.description || ""}
                    jobId={jobId ? parseInt(jobId, 10) : null}
                    candidateEmail={resolveCandidateContact(selectedResumeForEvaluation).email}
                    jobCompanyName={jobDetails?.company_name || null}
                    onGenerateQuestionnaire={handleQuestionnaireClick}
                    isGeneratingQuestionnaire={isGeneratingQuestionnaire}
                    onQuestionnaireReady={handleQuestionnaireReady}
                    onEvaluationSaved={async () => {
                        const data = await fetchJobDetails();
                        if (data?.resumes) {
                            setSelectedResumeForEvaluation((prev) => {
                                if (!prev?.id) return prev;
                                const fresh = data.resumes.find(
                                    (r) => Number(r.id) === Number(prev.id)
                                );
                                return fresh || prev;
                            });
                        }
                    }}
                />
            )}

            {isScheduleModalOpen && selectedResumeForSchedule && (
                <ScheduleInterviewModal
                    isOpen={isScheduleModalOpen}
                    onClose={handleCloseScheduleModal}
                    candidate={selectedResumeForSchedule}
                    onSchedule={handleScheduleSubmit}
                    jobId={jobId}
                />
            )}

            <FileViewerModal
                isOpen={isJDModalOpen}
                onClose={handleCloseJDModal}
                content={jdContent}
                filename={jdFilename}
            />

            <FileViewerModal
                isOpen={isResumeModalOpen}
                onClose={handleCloseResumeModal}
                content={resumeContent}
                filename={resumeFilename}
            />

            <FileViewerModal
                isOpen={isAnalysisModalOpen}
                onClose={handleCloseAnalysisModal}
                content={analysisContent}
                filename={analysisFilename}
            />
                </>
            )}
            
            {activeTab === 'application-form' && jobDetails && (
                <div style={{ padding: '2rem' }}>
                    {jobDetails.application_url ? (
                        <div className="application-form-exists">
                            <h3>Application Form is Active</h3>
                            <ApplicationUrlDisplay jobId={jobId} />
                            <div style={{ marginTop: '2rem' }}>
                                <Link to={`/jobs/${jobId}/setup-form`} className="edit-form-button">
                                    Edit Application Form
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="application-form-setup">
                            <h3>Set Up Application Form</h3>
                            <p>Configure your LinkedIn application form for this job posting.</p>
                            <Link to={`/jobs/${jobId}/setup-form`} className="setup-form-button">
                                Start Setup
                            </Link>
                        </div>
                    )}
                </div>
            )}
            
            {/* Old application form content - commented out for now */}
            {false && activeTab === 'application-form-old' && (
                <div className="application-form-tab">
                    <div className="glass-container" style={{ marginBottom: '1.5rem' }}>
                        <GreetingMessageEditor jobId={jobId} />
                    </div>
                    
                    <div className="glass-container" style={{ marginBottom: '1.5rem' }}>
                        <ApplicationQuestionsManager 
                            jobId={jobId} 
                            jobTitle={jobDetails ? (() => {
                                const desc = jobDetails.description || '';
                                const lines = desc.split('\n');
                                if (lines.length > 0) {
                                    let firstLine = lines[0].trim();
                                    firstLine = firstLine.replace(/^File:\s*/i, '').replace(/^Job description from file:\s*/i, '').trim();
                                    if (firstLine && firstLine.length < 200) {
                                        return firstLine;
                                    }
                                }
                                return 'Job Application';
                            })() : 'Job Application'}
                        />
                    </div>
                    
                    <div className="glass-container" style={{ marginBottom: '1.5rem' }}>
                        <FormCustomizationPanel jobId={jobId} />
                    </div>
                    
                    <div className="glass-container">
                        <ApplicationUrlDisplay jobId={jobId} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobDetailsPage; // testing-auto-refresh deployment
// Force deployment - auto-refresh and progress indicator fixes
console.log('QuestionnaireModal imported:', typeof QuestionnaireModal);
